import { CopySimple, UsersThree, LinkSimple, Handshake } from "@phosphor-icons/react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const PARTNERS = [
  { name: "Canterbury CC", area: "Canterbury", desc: "One of Kent's longest-running cycling clubs, with weekly road rides from the city centre. We support their club events and provide member servicing discounts." },
  { name: "Kent Cycling Association", area: "Kent-wide", desc: "The umbrella body for competitive cycling across Kent, organising the county's time trial and road race calendar. Ebikes&Gear supports KCA events as a technical partner." },
  { name: "Cycle Canterbury", area: "Canterbury", desc: "A community campaign group making everyday cycling safer and easier in Canterbury. We back their advocacy work and host free maintenance mornings for members." },
  { name: "East Kent CTC", area: "East Kent", desc: "The local Cycling UK member group running social rides across East Kent's lanes every week — all abilities welcome. Their riders get priority workshop booking with us." },
  { name: "Thanet Road Club", area: "Margate & Thanet", desc: "A friendly coastal road club with a proud racing heritage dating back decades. We supply event support and mechanical backup for their open time trials." },
  { name: "Medway Velo", area: "Medway", desc: "A modern, inclusive club covering road, gravel and e-bike rides across the Medway towns. Members enjoy exclusive fitting sessions at our Canterbury showroom." },
  { name: "Ashford Wheelers", area: "Ashford", desc: "Road and gravel riders exploring the Kent Downs from Ashford. We're their recommended e-bike specialist for members making the switch to electric." },
  { name: "Folkestone CC", area: "Folkestone", desc: "Coastal club known for its chaingang rides and hill sessions on the North Downs. Ebikes&Gear provides prize support for their annual hill climb." },
];

const SNIPPETS = [
  {
    label: "Simple text link",
    code: '<a href="https://ebikesandgear.co.uk/" title="Premium E-Bikes Kent — Specialized, Trek & Sur-Ron">Ebikes&Gear — Premium E-Bikes in Kent</a>',
  },
  {
    label: "Partner badge (styled)",
    code: '<a href="https://ebikesandgear.co.uk/" title="Ebikes&Gear — Premium E-Bikes Kent" style="display:inline-block;background:#EC2324;color:#fff;font-family:sans-serif;font-weight:700;font-size:13px;padding:8px 16px;border-radius:999px;text-decoration:none;">⚡ Proud partner of Ebikes&Gear</a>',
  },
];

export default function Partners() {
  const copy = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success("Snippet copied to clipboard"));
  };

  return (
    <>
      <SEO
        title="Partners & Cycling Clubs — Kent"
        description="The cycling clubs and community groups Ebikes&Gear works with across Kent — Canterbury CC, Kent Cycling Association, Medway Velo and more. Link to us with our partner badges."
        path="/partners"
      />
      <PageHero title="Our Partners" subtitle="We're proud to support Kent's cycling community — from race clubs to campaign groups. Riding is better together." image="/images/site/team.jpg" crumbs={[{ label: "Partners" }]} />

      <section className="container-px py-16" data-testid="partners-page">
        <Reveal className="max-w-2xl">
          <p className="overline">Clubs we work with</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Kent's cycling community</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">From event support and mechanical backup to member discounts and free maintenance sessions — here's who we ride with.</p>
        </Reveal>

        <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PARTNERS.map((p) => (
            <StaggerItem key={p.name}>
              <div className="h-full rounded-3xl border border-border bg-card p-6" data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary"><UsersThree size={22} weight="bold" /></span>
                <h3 className="mt-4 text-lg font-bold leading-tight">{p.name}</h3>
                <p className="mt-0.5 text-xs font-bold uppercase tracking-wider text-primary">{p.area}</p>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="bg-card border-y border-border py-16" data-testid="link-to-us-section">
        <div className="container-px">
          <Reveal className="max-w-2xl">
            <p className="overline">Link to us</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Add Ebikes&Gear to your club site</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">Partner clubs and cycling websites are welcome to link to us. Copy either snippet below and paste it into your website — it takes 30 seconds.</p>
          </Reveal>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {SNIPPETS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="rounded-3xl border border-border bg-background p-6" data-testid={`link-snippet-${i}`}>
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="inline-flex items-center gap-2 font-bold"><LinkSimple size={18} className="text-primary" /> {s.label}</h3>
                    <button onClick={() => copy(s.code)} className="btn-outline !px-4 !py-2 text-xs" data-testid={`copy-snippet-${i}`}>
                      <CopySimple size={14} /> Copy code
                    </button>
                  </div>
                  {i === 1 && (
                    <div className="mt-4">
                      <span className="inline-block rounded-full bg-primary px-4 py-2 text-[13px] font-bold text-primary-foreground">⚡ Proud partner of Ebikes&Gear</span>
                    </div>
                  )}
                  <pre className="mt-4 overflow-x-auto rounded-xl border border-border bg-muted/40 p-4 text-xs leading-relaxed text-foreground/80 whitespace-pre-wrap break-all">{s.code}</pre>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10">
            <div className="flex flex-wrap items-center gap-5 rounded-3xl bg-primary p-8 text-primary-foreground sm:justify-between">
              <div className="flex items-center gap-4">
                <Handshake size={34} weight="bold" />
                <div>
                  <h3 className="text-xl font-black">Run a club or cycling site?</h3>
                  <p className="mt-0.5 text-sm text-primary-foreground/85">Get in touch about partnerships, event support and member perks.</p>
                </div>
              </div>
              <a href="mailto:sales@ebikesandgear.co.uk?subject=Partnership enquiry" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-primary transition-transform hover:-translate-y-0.5" data-testid="partner-enquiry-btn">Become a partner</a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
